"use client";

import {
    useEffect,
    useRef,
    type TouchEvent,
    type HTMLAttributes,
} from "react";
import { isTouchDevice } from "~/utils/isTouchDevice";

type UseMarqueeOptions = {
  speed?: number;
  direction?: "left" | "right";
  pauseOnHover?: boolean;
  allowDrag?: boolean;
};

export const useMarquee = ({
    speed = 0.4,
    direction = "left",
    pauseOnHover = false,
    allowDrag = false,
}: UseMarqueeOptions = {}) => {
    const trackRef = useRef<HTMLDivElement | null>(null);
    const frameRef = useRef<number | null>(null);
    const offsetRef = useRef(0);
    const contentWidthRef = useRef(0);
    const pausedRef = useRef(false);

    /** drag state */
    const isDraggingRef = useRef(false);
    const dragStartXRef = useRef(0);
    const dragStartOffsetRef = useRef(0);
    const resumeTimeoutRef = useRef<number | null>(null);

    const resumeLater = () => {
        if (resumeTimeoutRef.current) {
            clearTimeout(resumeTimeoutRef.current);
        }

        resumeTimeoutRef.current = window.setTimeout(() => {
            pausedRef.current = false;
        }, 2500);
    };

    useEffect(() => {
        const track = trackRef.current;
        if (!track) return;

        contentWidthRef.current = track.scrollWidth / 2;

        const prefersReducedMotion = window.matchMedia(
            "(prefers-reduced-motion: reduce)"
        ).matches;

        if (prefersReducedMotion) return;

        offsetRef.current = direction === "right" ? contentWidthRef.current : 0;

        const step = () => {
            if (!pausedRef.current && !isDraggingRef.current) {
                offsetRef.current += direction === "left" ? speed : -speed;

                if (offsetRef.current >= contentWidthRef.current) {
                    offsetRef.current = 0;
                }

                if (offsetRef.current < 0) {
                    offsetRef.current = contentWidthRef.current;
                }

                track.style.transform = `translate3d(${-offsetRef.current}px, 0, 0)`;
            }

            frameRef.current = requestAnimationFrame(step);
        };

        frameRef.current = requestAnimationFrame(step);

        return () => {
            if (frameRef.current) cancelAnimationFrame(frameRef.current);
        };
    }, [speed, direction]);

    const hoverProps = pauseOnHover
        ? {
            onMouseEnter: () => (pausedRef.current = true),
            onMouseLeave: () => (pausedRef.current = false),
        }
        : {};

    const dragProps: HTMLAttributes<HTMLDivElement> = allowDrag && isTouchDevice()
        ? {
            onTouchStart: (e: TouchEvent<HTMLDivElement>) => {
                const touch = e.touches[0];
                if (!touch) return;

                isDraggingRef.current = true;
                pausedRef.current = true;
                dragStartXRef.current = touch.clientX;
                dragStartOffsetRef.current = offsetRef.current;
            },

            onTouchMove: (e: TouchEvent<HTMLDivElement>) => {
                if (!isDraggingRef.current) return;

                const touch = e.touches[0];
                if (!touch) return;

                const delta = touch.clientX - dragStartXRef.current;
                offsetRef.current = dragStartOffsetRef.current - delta;

          trackRef.current!.style.transform = `translate3d(${-offsetRef.current}px, 0, 0)`;
            },

            onTouchEnd: () => {
                isDraggingRef.current = false;
                resumeLater();
            },
        }
        : {};

    return {
        trackRef,
        hoverProps,
        dragProps,
    };
};
